import Minze, { Attrs, EventListeners, MinzeElement, Reactive } from "minze";
import { Locale } from "../../modules/locale";
import { EMAIL_REGEX, PHONE_REGEX } from "../../modules/utils";
import { submitForm } from "../../constants";
import { SquidexTextLabels } from "../../modules/squidex/labels";

Locale.getInstance().registerKeySet("fr", "contactForm", {
  nameLabel: "Nom",
  emailLabel: "Courriel",
  phoneLabel: "Téléphone (optionnel)",
  subjectLabel: "Sélectionnez un sujet",
  messageLabel: "Écrivez votre message...",
  send: "Envoyer",
  requiredError: "Requis",
  invalidEmailError: "Le courriel est invalide",
  invalidPhoneError: "Le numéro de téléphone est invalide",
  submittedMessage: "Votre message a été envoyé.",
  subjectLabels:
    "Questions et commentaires,Fondation,Formation,Médias,Support technique,Devenir bénévole d’affaires",
  donationSubjectLabels: "Don en actions,Don planifié,Don d'entreprise",
});

Locale.getInstance().registerKeySet("en", "contactForm", {
  nameLabel: "Name",
  emailLabel: "Email",
  phoneLabel: "Phone Number (optional)",
  subjectLabel: "Choose a topic",
  messageLabel: "Write a message",
  send: "Send",
  requiredError: "Required",
  invalidEmailError: "Email is invalid",
  invalidPhoneError: "Phone number is invalid",
  submittedMessage: "Your message has been sent.",
  subjectLabels:
    "Questions and Comments,Foundation,Training,Media,Technical Support,Become a business volunteer",
  donationSubjectLabels:
    "Donation of Shares,Planned Giving,Corporate Donations",
});

const subjectOptions = [
  "questions",
  "fondation",
  "formation",
  "médias",
  "support",
  "devenir bénévole d’affaires",
];
const donationSubjectOptions = [
  "don en actions",
  "don planifié",
  "don d'entreprise",
];

export interface TjContactForm {
  errors: { [fieldName: string]: string };
  values: { [fieldName: string]: any };
  submitted: boolean;
  donationForm: boolean;
}

export class TjContactForm extends MinzeElement {
  reactive: Reactive = [
    ["errors", {}],
    ["values", {}],
    ["submitted", false],
  ];
  attrs: Attrs = [["donationForm", false]];

  contactFormEmails: { [key: string]: string } = {
    questions: "info@teljeunes.com",
    fondation: "collectedefonds@fondationteljeunes.org",
    formation: "formation@teljeunes.com",
    médias: "communications@teljeunes.com",
    support: "info@teljeunes.com",
    "devenir bénévole d’affaires": "info@teljeunes.com",
  };

  submitForm = async () => {
    this.submitted = false;
    this.errors = {};
    const nameField = this.select(`#name`)?.getAttribute("value");
    const emailField = this.select(`#email`)?.getAttribute("value");
    const phoneField = this.select(`#phone`)?.getAttribute("value");
    const subjectField = this.select(`#subject`)?.getAttribute("value");
    const messageField = this.select(`#message`)?.getAttribute("value");
    this.values = {
      name: nameField,
      email: emailField,
      phone: phoneField,
      subject: subjectField,
      message: messageField,
    };
    if (!nameField)
      this.errors = {
        ...this.errors,
        name: SquidexTextLabels.getKey("contactform.requirederror"),
      };
    if (!emailField)
      this.errors = {
        ...this.errors,
        email: SquidexTextLabels.getKey("contactform.requirederror"),
      };
    else if (!EMAIL_REGEX.test(emailField)) {
      this.errors = {
        ...this.errors,
        email: SquidexTextLabels.getKey("contactform.invalidemailerror"),
      };
    }
    if (!subjectField)
      this.errors = {
        ...this.errors,
        subject: SquidexTextLabels.getKey("contactform.requirederror"),
      };
    if (!messageField)
      this.errors = {
        ...this.errors,
        message: SquidexTextLabels.getKey("contactform.requirederror"),
      };
    if (phoneField && !PHONE_REGEX.test(phoneField)) {
      this.errors = {
        ...this.errors,
        phone: SquidexTextLabels.getKey("contactform.invalidphoneerror"),
      };
    }
    if (!Object.values(this.errors).length) {
      const response = await submitForm(
        this.donationForm ? "354g1j9" : "354kica",
        this.donationForm
          ? { ...this.values }
          : {
              ...this.values,
              sendTo: this.contactFormEmails[this.values["subject"]],
            },
      );
      if (response.status === "success") {
        this.submitted = true;
        this.values = {};
        Minze.cast("tj:contact-form-submitted");
        this.rerender(true);
      }
    }
    this.rerender();
  };
  html = () => `
    <tj-box mb="16px">
      <tj-input
        id="name"
        name="name"
        placeholder="${SquidexTextLabels.getKey("contactform.namelabel")}"
        error="${this.errors["name"]}"
        value="${this.values["name"]}"
      ></tj-input>
    </tj-box>
    <tj-box mb="16px">
      <tj-input
        id="email"
        name="email"
        type="email"
        placeholder="${SquidexTextLabels.getKey("contactform.emaillabel")}"
        error="${this.errors["email"]}"
        value="${this.values["email"]}"
      ></tj-input>
    </tj-box>
    <tj-box mb="16px">
      <tj-input
        id="phone"
        name="phone"
        placeholder="${SquidexTextLabels.getKey("contactform.phonelabel")}"
        error="${this.errors["phone"]}"
        value="${this.values["phone"]}"
      ></tj-input>
    </tj-box>
    <tj-box mb="16px">
      <tj-select
        id="subject"
        name="subject"
        placeholder="${SquidexTextLabels.getKey("contactform.subjectlabel")}"
        default-value=""
        selectOptions="${(this.donationForm ? donationSubjectOptions : subjectOptions).join(",")}"
        selectOptionLabels="${SquidexTextLabels.getKey(this.donationForm ? "contactform.donationsubjectlabels" : "contactform.subjectlabels")}"
        error="${this.errors["subject"]}"
        value="${this.values["subject"]}"
      ></tj-select>
    </tj-box>
    <tj-box mb="16px">
      <tj-input
        id="message"
        name="message"
        textarea="true"
        placeholder="${SquidexTextLabels.getKey("contactform.messagelabel")}"
        error="${this.errors["message"]}"
        value="${this.values["message"]}"
      ></tj-input>
    </tj-box>
    <tj-box mb="16px" display="flex" alignItems="center" justifyContent="space-between">
      <tj-button id="submit" name="save" size="small" width="fit-content"
      class="contact-form-submit-button"
      >
        ${SquidexTextLabels.getKey("contactform.send")}
      </tj-button>
      ${this.submitted ? `<tj-text>${SquidexTextLabels.getKey("contactform.submittedmessage")}</tj-text>` : ""}
    </tj-box>
  `;

  css = () => `
    .title {
      font-weight: 500;
      font-size: 40px;
      line-height: 120%;
      color: #1E007D;
      margin: 80px 0 8px 0;
    }
  `;
  eventListeners: EventListeners = [["#submit", "click", this.submitForm]];
}
